import { Facade } from "@/drawing";
import type { LayerInfo } from "@/drawing/managers";
import CanvasSettingsState from "@/stores/CanvasSettingsState";
import { Logger } from "@/logger";
import { ref } from "vue";

// Display layer with visibility property for template compatibility
interface DisplayLayer extends LayerInfo {
  visibility: boolean;
  colorCss: string;
}

const layers = ref<DisplayLayer[]>([]);

export function useLayersList() {
  const updateLayers = () => {
    if (!Facade.isInitialized) return;
    layers.value = Facade.layers.getAll().map((layer) => ({
      ...layer,
      visibility: layer.isVisible,
      colorCss: layer.colorCss ?? "rgb(128,128,128)",
    }));
  };

  const toggleLayerVisibility = (layer: DisplayLayer) => {
    Logger.info(
      `toggleLayerVisibility: Toggling current layer visibility ${JSON.stringify(
        layer
      )} `
    );
    if (!Facade.isInitialized) return;

    const newVisibility = Facade.layers.toggleVisibility(layer.name);
    Logger.info(
      `toggleLayerVisibility : Toggled ${layer.name} visibility to ${newVisibility}`
    );
    layer.visibility = newVisibility;
  };

  /** Verrouille/déverrouille un calque : verrouillé = visible mais non-sélectionnable. */
  const toggleLayerLock = (layer: DisplayLayer) => {
    if (!Facade.isInitialized) return;
    layer.isLocked = Facade.layers.toggleLocked(layer.name);
  };

  /** Verrouille les calques hors légende, déverrouille ceux de la légende. */
  const lockNonLegend = () => {
    if (!Facade.isInitialized) return;
    const legend = new Set(
      (CanvasSettingsState.activeLegend.layer_configs ?? []).map((l) => l.name)
    );
    for (const layer of layers.value) {
      const shouldLock = !legend.has(layer.name);
      if (layer.isLocked !== shouldLock) {
        Facade.layers.setLocked(layer.name, shouldLock);
        layer.isLocked = shouldLock;
      }
    }
  };

  const unlockAll = () => {
    if (!Facade.isInitialized) return;
    for (const layer of layers.value) {
      if (layer.isLocked) {
        Facade.layers.setLocked(layer.name, false);
        layer.isLocked = false;
      }
    }
  };

  return {
    layers,
    updateLayers,
    toggleLayerVisibility,
    toggleLayerLock,
    lockNonLegend,
    unlockAll,
  };
}
